import { Navigate, useParams } from 'react-router-dom';
import useUser from '../../custom-hook/use-user';
import { buildCompanyPath, buildInstallationPath } from '../../helper/url-builder';

/**
 * LegacyRedirect - Redirects old URL patterns to new parameterized URLs
 *
 * This component handles backward compatibility for bookmarked URLs
 * that use the old pattern (e.g., /app/about-companies) by redirecting
 * to the new pattern (e.g., /app/company/123/about).
 *
 * @param {string} targetRoute - The route key to redirect to
 */
function LegacyRedirect({ targetRoute }) {
    const { getCompanyId, getUserInstallationId } = useUser();
    const params = useParams();
    const companyId = getCompanyId();

    // Prevent redirect if no company context available - send to login
    if (!companyId) {
        return <Navigate to="/login" replace />;
    }

    const installationId = getUserInstallationId();
    const newPath = getRedirectPath(targetRoute, companyId, installationId, params);

    return <Navigate to={newPath} replace />;
}

function getRedirectPath(targetRoute, companyId, installationId, params = {}) {
    // Company-level routes
    if (buildCompanyPath[targetRoute]) {
        return buildCompanyPath[targetRoute](companyId);
    }

    // Installation-level routes (require installationId)
    if (buildInstallationPath[targetRoute]) {
        if (installationId) {
            if (targetRoute === "driverDetail" || targetRoute === "driverChargepoints") {
                if (!params?.driverId) {
                    return buildInstallationPath.drivers(companyId, installationId);
                }
                return buildInstallationPath[targetRoute](companyId, installationId, params.driverId);
            }
            if (targetRoute === "controllerDetail" || targetRoute === "controllerSensing" || targetRoute === "controllerTopology") {
                if (!params?.controllerId) {
                    return buildInstallationPath.controllers(companyId, installationId);
                }
                return buildInstallationPath[targetRoute](companyId, installationId, params.controllerId);
            }
            return buildInstallationPath[targetRoute](companyId, installationId);
        }
        // No installation selected, redirect to company about page
        return buildCompanyPath.about(companyId);
    }

    // Unknown route, default to about
    return buildCompanyPath.about(companyId);
}

export default LegacyRedirect;
